import { useCallback, useEffect, useRef, useState } from "react";

const COOKIE_NAME = "sidebar-collapsed";

interface UseSidebarCollapsedOptions {
    /**
     * When true, forces the sidebar to collapsed state regardless of user preference.
     * User preference is still persisted but overridden while forceCollapsed is true.
     */
    forceCollapsed?: boolean;
    /**
     * AIDEV-NOTE: Server-read cookie value, passed from root loader via DesktopLayout.
     * Allows SSR to render the correct sidebar state without hydration flash.
     */
    serverDefault?: boolean;
}

/**
 * Hook to manage sidebar collapsed state with cookie persistence.
 * Defaults to expanded (false) on first visit.
 * Returns shouldAnimate=false during initial hydration to prevent animation flicker.
 *
 * AIDEV-NOTE: Cookie-based instead of localStorage so the server can read the value
 * during SSR and render the correct layout without a flash of wrong state.
 * When forceCollapsed is true, the effective isCollapsed state is always true,
 * but user preference is still tracked in the cookie. This allows routes like /admin
 * to force collapsed state while preserving user preference for other routes.
 */
export function useSidebarCollapsed(options: UseSidebarCollapsedOptions = {}) {
    const { forceCollapsed = false, serverDefault = false } = options;

    // AIDEV-NOTE: Initialize from serverDefault (cookie value read by root loader).
    // This ensures SSR and initial client render match, preventing hydration flash.
    const [userPreference, setUserPreference] = useState(serverDefault);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const hasHydrated = useRef(false);

    // Enable animations after initial hydration settles
    useEffect(() => {
        if (!hasHydrated.current) {
            hasHydrated.current = true;
            // Small delay to ensure initial render is complete before enabling animations
            const timer = setTimeout(() => {
                setShouldAnimate(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []);

    // Persist to cookie when state changes
    const toggleCollapsed = useCallback(() => {
        setUserPreference((prev) => {
            const next = !prev;
            document.cookie = `${COOKIE_NAME}=${next}; path=/; max-age=31536000; SameSite=Lax`;
            return next;
        });
    }, []);

    // AIDEV-NOTE: Effective collapsed state respects forceCollapsed override
    const isCollapsed = forceCollapsed || userPreference;

    return {
        isCollapsed,
        toggleCollapsed,
        shouldAnimate,
        /** The user's actual preference, regardless of forceCollapsed */
        userPreference,
    };
}
